import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { internalLayerVersionsKeys } from 'hooks/internalLayerVersion/queryKeys';
import { ItemType } from 'constants/itemType';

interface RequestApprovalsData {
  itemId: string;
  itemType: ItemType;
  reviewers: {
    reviewerItemId: string;
  }[];
  groupId: string;
  approvalsRequiredInOrder?: boolean;
  incidentId?: string;
}

type EligibleApprovalReviewer = {
  reviewerItemId: string;
  displayName: string;
};

export const useEligibleApprovalReviewers = (values: {
  itemId: string;
  enabled?: boolean;
}) => {
  const { itemId, enabled = true } = values;

  return useQuery({
    queryKey: ['sitreps', 'eligibleReviewers', itemId],
    enabled: enabled && !!itemId,
    queryFn: async () => {
      const response = await axios.get<EligibleApprovalReviewer[]>(
        config.apiGateway.sitrepEligibleReviewers(itemId)
      );
      return response.data;
    },
  });
};

export const useRequestApprovals = (
  queryKeysToInvalidate?: readonly string[],
  options?: {
    suppressSuccessToast?: boolean;
    deferQueryInvalidation?: boolean;
  }
) => {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    //TODO: Narrow the invalidation to relevant queries only
    queryClient.invalidateQueries({ queryKey: ['approvals'] });
    if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
      queryClient.invalidateQueries({ queryKey: queryKeysToInvalidate });
    }
  };

  const mutation = useMutation({
    mutationFn: async (data: RequestApprovalsData) => {
      const response = await axios.post<{ success: boolean; message: string }>(
        `${config.apiGateway.approvals}/${data.itemType}/${data.itemId}/request`,
        {
          reviewers: data.reviewers,
          approvalsRequiredInOrder: data.approvalsRequiredInOrder,
          groupId: data.groupId,
          incidentId: data.incidentId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      if (!options?.suppressSuccessToast) {
        // rymi-TODO: Show name of item in toast?
        toast.success('Approval request sent');
      }
      if (!options?.deferQueryInvalidation) {
        invalidateQueries();
      }
    },
    onError: (error: any) => {
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.error?.code === 'APPROVAL_ALREADY_SUBMITTED'
      ) {
        toast.error('A user has already submitted the request');
        invalidateQueries();
        return;
      }
      const payload = error.response?.data;
      const errorMessage =
        (typeof payload?.message === 'string' && payload.message) ||
        (typeof payload?.error === 'string' && payload.error) ||
        'Failed to send approval request.';
      toast.error(errorMessage);
    },
  });

  return Object.assign(mutation, { invalidateQueries });
};

export const useApproveRequest = (
  queryKeysToInvalidate?: readonly string[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      itemType: ItemType;
      itemId: string;
      groupId: string;
      incidentId?: string;
      comments?: string;
    }) => {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${config.apiGateway.approvals}/${data.itemType}/${data.itemId}/approve`,
        {
          groupId: data.groupId,
          incidentId: data.incidentId,
          comments: data.comments,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Request has been approved');
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeysToInvalidate });
      }
      queryClient.invalidateQueries({
        queryKey: internalLayerVersionsKeys.all(),
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || 'Failed to approve the item.';
      toast.error(errorMessage);
    },
  });
};

export const useApproveWithoutReview = (
  queryKeysToInvalidate?: readonly string[]
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      itemType: ItemType;
      itemId: string;
      groupId: string;
    }) => {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${config.apiGateway.approvals}/${data.itemType}/${data.itemId}/approveWithoutReview`,
        {
          groupId: data.groupId,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Map layer published');
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeysToInvalidate });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message ||
        'Failed to approve the item without review.';
      toast.error(errorMessage);
    },
  });
};

export const useRejectRequest = (queryKeysToInvalidate?: readonly string[]) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      itemType: ItemType;
      itemId: string;
      groupId: string;
      incidentId?: string;
      comments?: string;
    }) => {
      const response = await axios.put<{ success: boolean; message: string }>(
        `${config.apiGateway.approvals}/${data.itemType}/${data.itemId}/reject`,
        {
          groupId: data.groupId,
          incidentId: data.incidentId,
          comments: data.comments,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Request has been rejected');
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
      if (queryKeysToInvalidate && queryKeysToInvalidate.length > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeysToInvalidate });
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || 'Failed to reject the item.';
      toast.error(errorMessage);
    },
  });
};
